"use client";
import { useState } from "react";

interface Tab {
  title: string;
  content: React.ReactNode;
}

interface TabsProps {
  tabs: Tab[];
  selected?: number;
  onSelect?: (index: number) => void;
}

export function Tabs({ tabs, selected, onSelect }: TabsProps) {
  const [internalTab, setInternalTab] = useState<number>(0);
  const activeTab = selected ?? internalTab;

  const selectTab = (index: number) => {
    if (onSelect) {
      onSelect(index);
      return;
    }
    setInternalTab(index);
  };

  return (
    <div className="w-full">
      <div className="border-b border-gray-300">
        <nav
          role="tablist"
          aria-label="Tabs"
          className="flex space-x-2 md:space-x-8 overflow-x-auto"
        >
          {tabs.map((tab, index) => (
            <button
              // Positional key: titles often embed live counts ("4 Active"),
              // and keying on them remounts the buttons on every count change.
              key={index}
              type="button"
              role="tab"
              id={`tabs-trigger-${index}`}
              aria-selected={activeTab === index}
              aria-controls={`tabs-content-${index}`}
              className={`${
                activeTab === index
                  ? "text-blue-500 border-blue-500"
                  : "border-transparent text-gray-600 hover:border-gray-300 hover:text-gray-700"
              } text-sm whitespace-nowrap border-b-2 py-4 px-0.5 cursor-pointer`}
              onClick={() => selectTab(index)}
            >
              {tab.title}
            </button>
          ))}
        </nav>
      </div>
      {/*
        `key` forces a remount when the tab changes. Without it, two tabs whose
        content is the same component type (e.g. a job table rendered with
        different `status` props) reconcile as a prop update, so any state the
        component seeded from its props on mount goes stale and effects keyed on
        that state never re-run.
      */}
      <div
        key={activeTab}
        role="tabpanel"
        id={`tabs-content-${activeTab}`}
        aria-labelledby={`tabs-trigger-${activeTab}`}
      >
        {tabs[activeTab]?.content}
      </div>
    </div>
  );
}
